import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Gift, Tag } from "lucide-react";
import { api, inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { SectionHead } from "../components/Motion";

const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">{label}</span>
    <input {...props} className="mt-1.5 w-full bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]" />
  </label>
);

export default function Checkout() {
  const { cart, settings, clearCart, user } = useStore();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ name: user?.name || "", phone: "", email: user?.email || "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [giftMsg, setGiftMsg] = useState({ recipient: "", sender: "", message: "" });
  const [wantMsg, setWantMsg] = useState(false);
  const [giftWrap, setGiftWrap] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);

  useEffect(() => {
    if (user) api.get("/customer/addresses").then((r) => setSavedAddresses(r.data.addresses)).catch(() => {});
  }, [user]);

  const applySaved = (a) => {
    setAddress({ name: a.name, phone: a.phone, email: address.email || user?.email || "", line1: a.house, line2: [a.street, a.landmark].filter(Boolean).join(", "), city: a.city, state: a.state, pincode: a.pincode });
    toast.success("Saved address applied");
  };

  useEffect(() => { api.post("/events", { type: "begin_checkout", meta: { items: cart.length } }).catch(() => {}); }, []);

  const subtotal = cart.reduce((a, i) => {
    return a + i.price * i.qty;
  }, 0);
  const bundleSavings = cart.reduce((a, i) => a + 0, 0);
  const wrapFee = giftWrap ? settings?.gift_wrap_price ?? 99 : 0;
  const discount = applied?.discount || 0;
  const estTotal = subtotal - discount + wrapFee;

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied(data);
      toast.success(`${data.code} applied — you save ${inr(data.discount)}`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e));
    }
  };

  const openRazorpay = async (order, keyId) => {
    const ok = await loadRazorpay();
    if (!ok) {
      setPayError({ order, keyId, message: "Could not load the payment gateway. Check your connection and retry." });
      setPaying(false);
      return;
    }
    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.total * 100,
      currency: "INR",
      name: "SAVYRON",
      description: order.order_id,
      order_id: order.razorpay_order_id,
      prefill: { name: address.name, email: address.email, contact: address.phone },
      theme: { color: "#7E1E3F" },
      handler: async (resp) => {
        try {
          await api.post("/checkout/verify", {
            order_id: order.order_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          finish(order);
        } catch (err) {
          setPayError({ order, keyId, message: "Payment verification failed on our server. If money was debited, Razorpay auto-refunds it — write to founder@savyron.in with your order ID." });
          setPaying(false);
        }
      },
      modal: { ondismiss: () => {
        setPayError({ order, keyId, message: "Payment was cancelled before completion. Your order is saved — you can retry safely." });
        setPaying(false);
      } },
    });
    rzp.on("payment.failed", (resp) => {
      setPayError({ order, keyId, message: resp?.error?.description || "Payment failed. No amount was captured — please retry." });
      setPaying(false);
    });
    rzp.open();
  };

  const pay = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setPaying(true);
    setPayError(null);
    try {
      const payload = {
        items: cart.map((i) => ({ slug: i.slug, qty: i.qty, ...(i.personalization ? { personalization: i.personalization } : {}) })),
        address, gift_wrap: giftWrap,
        gift_message: wantMsg ? giftMsg : null,
        coupon: applied?.code || null,
      };
      const { data } = await api.post("/checkout/create-order", payload);
      const order = data.order;

      if (data.payment_mode === "razorpay" && data.razorpay_key_id) {
        openRazorpay(order, data.razorpay_key_id);
      } else {
        const res = await api.post("/checkout/demo-pay", { order_id: order.order_id });
        finish(res.data.order);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  const finish = (order) => {
    api.post("/events", { type: "purchase", meta: { order_id: order.order_id, total: order.total } }).catch(() => {});
    clearCart();
    navigate(`/order/${order.order_id}`, { state: { email: order.email } });
  };

  if (cart.length === 0) return (
    <div className="pt-40 pb-24 text-center min-h-screen" data-testid="checkout-empty">
      <p className="font-serif-lux text-3xl text-[#857078]">Your cart is empty</p>
      <Link to="/gifts" className="inline-block mt-6 text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-3">Explore Gifts</Link>
    </div>
  );

  return (
    <div className="pt-28 pb-24 max-w-6xl mx-auto px-4 sm:px-6" data-testid="checkout-page">
      <SectionHead overline="Secure Checkout" title="Almost Theirs" sub="Prepaid orders only. We never store card, CVV, OTP or UPI details." testid="checkout-head" />
      {payError && (
        <div className="mb-8 card-surface rounded-2xl p-6 border-[#E63946]/50 flex flex-wrap items-center gap-4" data-testid="payment-failure">
          <div className="flex-1 min-w-[220px]">
            <p className="font-serif-lux text-2xl text-[#E63946]">Payment Not Completed</p>
            <p className="text-sm text-[#857078] mt-1.5 leading-relaxed">{payError.message}</p>
            <p className="text-xs text-[#857078] mt-2">Order ID: <span className="font-mono-lux text-[#45303A]">{payError.order.order_id}</span> · No amount has been captured.</p>
          </div>
          <button onClick={() => { setPayError(null); setPaying(true); openRazorpay(payError.order, payError.keyId); }}
            className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-3.5 hover:bg-[#5C1230] transition-colors"
            data-testid="payment-retry-button">Retry Payment</button>
        </div>
      )}
      <form onSubmit={pay} className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div className="card-surface rounded-2xl p-6 space-y-4">
            <h3 className="font-serif-lux text-xl text-[#2E1A22]">Delivery Address</h3>
            {savedAddresses.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="saved-address-picker">
                {savedAddresses.map((a) => (
                  <button type="button" key={a.id} onClick={() => applySaved(a)}
                    className={`text-[11px] border rounded-full px-3.5 py-2 transition-colors ${a.is_default ? "border-[#7E1E3F] text-[#7E1E3F] font-medium" : "border-[#E5E7EB] text-[#857078] hover:border-[#7E1E3F]/50"}`}
                    data-testid={`saved-address-${a.id}`}>
                    {a.city}, {a.state} – {a.pincode}{a.is_default ? " ★" : ""}
                  </button>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} data-testid="address-name" />
              <Field label="Phone" required value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} data-testid="address-phone" />
            </div>
            <Field label="Email" type="email" required value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} data-testid="address-email" />
            <Field label="Address Line 1" required value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} data-testid="address-line1" />
            <Field label="Address Line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} data-testid="address-line2" />
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="City" required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="address-city" />
              <Field label="State" required value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} data-testid="address-state" />
              <Field label="Pincode" required inputMode="numeric" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="address-pincode" />
            </div>
          </div>

          <div className="card-surface rounded-2xl p-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={wantMsg} onChange={(e) => setWantMsg(e.target.checked)} className="accent-[#7E1E3F] w-4 h-4" data-testid="gift-message-toggle" />
              <span className="text-sm text-[#45303A] flex items-center gap-2"><Gift size={15} className="text-[#7E1E3F]" /> Add a free gift message</span>
            </label>
            {wantMsg && (
              <div className="mt-4 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Recipient Name" value={giftMsg.recipient} onChange={(e) => setGiftMsg({ ...giftMsg, recipient: e.target.value })} data-testid="gift-recipient" />
                  <Field label="Sender Name" value={giftMsg.sender} onChange={(e) => setGiftMsg({ ...giftMsg, sender: e.target.value })} data-testid="gift-sender" />
                </div>
                <label className="block">
                  <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Your Message</span>
                  <textarea rows={3} value={giftMsg.message} onChange={(e) => setGiftMsg({ ...giftMsg, message: e.target.value })}
                    className="mt-1.5 w-full bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60" data-testid="gift-message-text" />
                </label>
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer mt-5 pt-5 border-t border-[#7E1E3F]/10">
              <input type="checkbox" checked={giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} className="accent-[#7E1E3F] w-4 h-4" data-testid="gift-wrap-toggle" />
              <span className="text-sm text-[#45303A]">Add Premium Gift Wrapping <span className="text-[#7E1E3F]">+{inr(settings?.gift_wrap_price ?? 99)}</span></span>
            </label>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card-surface rounded-2xl p-6 sticky top-24" data-testid="order-summary">
            <h3 className="font-serif-lux text-xl text-[#2E1A22] mb-5">Order Summary</h3>
            <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
              {cart.map((i) => (
                <div key={i.key || i.slug} className="flex gap-3 items-center">
                  <img src={i.image} alt="" className="w-12 h-14 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#45303A] line-clamp-1">{i.name}</p>
                    <p className="text-[11px] text-[#857078]">Qty {i.qty}{i.qty >= 2 && i.slug.includes("sonata") ? " · bundle applied" : ""}</p>
                    {i.personalization && (
                      <p className="text-[11px] text-[#B4853B] flex items-center gap-1.5" data-testid={`summary-pers-${i.key}`}>
                        {i.personalization.photo && <img src={i.personalization.photo} alt="" className="w-4 h-4 rounded object-cover" />}
                        For “{i.personalization.name}”
                      </p>
                    )}
                  </div>
                  <span className="text-sm text-[#2E1A22]">{inr(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code"
                className="flex-1 bg-[#FFFFFF] border border-[#7E1E3F]/25 rounded-full px-4 py-2.5 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
                data-testid="coupon-input" />
              <button type="button" onClick={applyCoupon} className="text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-4 hover:bg-[#7E1E3F]/10" data-testid="coupon-apply">
                <Tag size={14} />
              </button>
            </div>
            {applied && <p className="text-xs text-[#2A9D8F] mt-2">{applied.label}</p>}
            <div className="mt-5 pt-5 border-t border-[#7E1E3F]/10 space-y-2 text-sm">
              <div className="flex justify-between text-[#857078]"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#2A9D8F]"><span>Coupon ({applied?.code})</span><span>−{inr(discount)}</span></div>}
              {giftWrap && <div className="flex justify-between text-[#857078]"><span>Gift Wrapping</span><span>{inr(wrapFee)}</span></div>}
              <div className="flex justify-between text-[#857078]"><span>Delivery</span><span className="text-[#2A9D8F]">Free</span></div>
              <div className="flex justify-between text-base text-[#2E1A22] font-semibold pt-2"><span>Total</span><span data-testid="checkout-total">{inr(estTotal)}</span></div>
              <p className="text-[11px] text-[#857078]">Bundle discounts are calculated securely at payment.</p>
            </div>
            <div className="mt-5 pt-5 border-t border-[#7E1E3F]/10 space-y-2.5" data-testid="payment-methods">
              <p className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Payment Method</p>
              <label className="flex items-center gap-3 border border-[#7E1E3F] bg-[#7E1E3F]/5 rounded-xl px-4 py-3 cursor-pointer" data-testid="paymethod-razorpay">
                <input type="radio" name="paymethod" checked readOnly className="accent-[#7E1E3F] w-4 h-4" />
                <span className="text-sm text-[#2E1A22] font-medium">Razorpay</span>
                <span className="text-[11px] text-[#857078] ml-auto">UPI · Cards · Net Banking</span>
              </label>
              <label className="flex items-center gap-3 border border-[#E5E7EB] rounded-xl px-4 py-3 opacity-50 cursor-not-allowed" data-testid="paymethod-cod">
                <input type="radio" name="paymethod" disabled className="accent-[#7E1E3F] w-4 h-4" />
                <span className="text-sm text-[#857078]">Cash on Delivery</span>
              </label>
            </div>
            <button type="submit" disabled={paying}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-[#7E1E3F] text-[#FFFFFF] font-semibold text-sm tracking-wider uppercase rounded-full py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60 active:scale-[0.99]"
              data-testid="pay-order-btn">
              {paying ? "Processing…" : <>Pay {inr(estTotal)} Securely <ArrowRight size={16} /></>}
            </button>
            <p className="text-[11px] text-[#857078] mt-3 text-center">Secured by Razorpay · UPI · Cards · Net Banking · Wallets</p>
          </div>
        </div>
      </form>
    </div>
  );
}
